import { g_listOrigin } from "../core/variables-global.js";
import helpers from "../utils/helpers.js";
import dataExchange from "./dataExchange.js";

function IndexedDBManager() {
  // Configuration et variables principales
  const DB_NAME = "ThermoDataDB";
  const DEFAULT_DB_VERSION = 1;
  const RETRY_DELAY = 100;
  const MAX_RETRY_ATTEMPTS = 50;

  const REQUIRED_INDEXES = {
    "neep": ["ahri", "marque", "modele_exterieur", "modele_interieur", "puissance_nominale", "puissance_moins_8", "aide_financiere_a", "aide_financiere_b", "haut_rendement"],
    "es": ["pd_id", "ahri_reference_number", "outdoor_unit_brand_name", "model_number", "indoor_unit_model_number", "furnace_model_number", "cooling_capacity_btu_h", "heating_capacity_at_17_f_btu_h", "cold_climate", "TTG_AF_EffAlone", "TTG_AF_Maison1", "TTG_AF_Multi1", "TTG_AF_Multi4", "TTG_Ext2", "TTG_Ext5", "TTG_colBonification"]
  };

  let activeList = { file: "", name: "" };
  const dbName = DB_NAME;
  let dataO;
  let db = null;
  let dataset = null;
  let dbVersion = DEFAULT_DB_VERSION;
  const eventListeners = new Map();
  let retryAttempts = 0;
  let isDataReady = false; // Flag pour indiquer si les données sont prêtes

  // Cache mémoire des données par dataset. Une fois le dataset chargé (depuis IndexedDB
  // ou fraîchement sauvegardé), toutes les recherches/tris/paginations suivants sont
  // effectués en mémoire (Array.filter/sort/slice) plutôt que de rouvrir une transaction
  // et un curseur IndexedDB asynchrone à chaque interaction utilisateur.
  const memoryCache = new Map();
  const setMemoryCache = (datasetName, items) => {
    if (datasetName && Array.isArray(items)) {
      memoryCache.set(datasetName, items);
    }
  };
  const getMemoryCache = (datasetName) => memoryCache.get(datasetName) || null;

  // Détecte si le navigateur est Safari
  const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("chromium");
  };

  // Initialise le gestionnaire et configure les écouteurs d'événements
  const init = async () => {
    console.log("Initialisation d'IndexedDBManager");
    try {
      if (!window.indexedDB) {
        console.error("IndexedDB n'est pas supporté par ce navigateur");
        return;
      }

      dbVersion = await getCurrentDbVersion();
      setupDataLoaderListener();

    } catch (error) {
      console.error("Erreur lors de l'initialisation:", error);
      setupDataLoaderListener();
    }
  };

  // Détecte la version actuelle de la base de données - corrigé pour éviter double résolution
  const getCurrentDbVersion = () => {
    return new Promise((resolve) => {
      let resolved = false;
      const safeResolve = (value) => {
        if (!resolved) {
          resolved = true;
          resolve(value);
        }
      };

      try {
        const request = indexedDB.open(dbName);

        request.onsuccess = ({ target }) => {
          const database = target.result;
          const currentVersion = database.version;
          database.close();
          safeResolve(currentVersion);
        };

        request.onerror = ({ target }) => {
          console.warn("Erreur lors de la détection de version DB:", target.error);
          safeResolve(1);
        };

        request.onblocked = () => {
          console.warn("Détection version bloquée");
          safeResolve(1);
        };

        // Safari peut bloquer sans réponse - timeout de sécurité
        setTimeout(() => {
          safeResolve(1);
        }, 5000);

      } catch (error) {
        console.warn("Exception lors de la détection de version DB:", error);
        safeResolve(1);
      }
    });
  };

  // Configure l'écouteur pour DataLoader - version non-récursive avec setTimeout
  const setupDataLoaderListener = () => {
    const dataLoadedHandler = (eventData) => {
      activeList.file = eventData.dataset;
      dataO = eventData.data;
      dataset = activeList.file;
      verifyDbExist(dataset);
    };

    const attemptSetup = () => {
      if (dataExchange) {
        retryAttempts = 0;
        dataExchange.addEventListener("dataLoaded", dataLoadedHandler);
      } else if (retryAttempts < MAX_RETRY_ATTEMPTS) {
        retryAttempts++;
        setTimeout(attemptSetup, RETRY_DELAY);
      } else {
        console.warn("DataExchange non disponible après maximum de tentatives");
      }
    };

    attemptSetup();
  };

  // Ajoute un écouteur d'événement
  const addEventListener = (eventType, callback) => {
    if (typeof callback !== "function") {
      console.warn("Le callback doit être une fonction");
      return;
    }

    if (!eventListeners.has(eventType)) {
      eventListeners.set(eventType, []);
    }
    eventListeners.get(eventType).push(callback);
  };

  // Émet un événement vers tous les écouteurs
  const emitEvent = (eventType, data) => {
    const callbacks = eventListeners.get(eventType);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erreur dans l'écouteur ${eventType}:`, error);
        }
      });
    }
  };

  // Vérifie l'existence de la base de données et charge ou crée les données
  const verifyDbExist = async (dataset) => {
    if (!dataset) {
      console.warn("Aucun dataset fourni");
      return [];
    }

    try {
      // Marquer les données comme non prêtes pendant le chargement
      isDataReady = false;

      await openDB(dataset);

      const cachedData = await loadDataFromDB(dataset);
      if (cachedData?.length > 0) {
        // Données chargées depuis le cache - marquer comme prêtes
        setMemoryCache(dataset, cachedData);
        isDataReady = true;
        emitEvent("dataReady", { dataset, data: cachedData, source: "cache" });
        return cachedData;
      }

      if (!dataO || !Array.isArray(dataO) || dataO.length === 0) {
        console.warn("Aucune donnée à sauvegarder");
        isDataReady = true;
        emitEvent("dataReady", { dataset, data: [], source: "empty" });
        return [];
      }

      // Rendre les données disponibles immédiatement (cache mémoire + événement) sans
      // attendre la persistance IndexedDB, qui n'est utile qu'au prochain chargement de
      // page. La sauvegarde s'exécute en arrière-plan pour ne pas retarder l'affichage.
      setMemoryCache(dataset, dataO);
      isDataReady = true;
      emitEvent("dataReady", { dataset, data: dataO, source: "fresh" });

      saveDataToDB(dataset, dataO).catch((error) => {
        console.error(`Erreur lors de la sauvegarde différée dans IndexedDB pour ${dataset}:`, error);
      });

      return dataO;
    } catch (error) {
      console.error(`Erreur lors de la vérification de la DB pour ${dataset}:`, error);
      isDataReady = true; // Permettre de continuer même en cas d'erreur
      emitEvent("dataReady", { dataset, data: [], source: "error", error });
      return [];
    }
  };

  // Ferme proprement la connexion à la base de données
  const closeDB = () => {
    if (db) {
      try {
        db.close();
      } catch (e) {
        console.warn("Erreur lors de la fermeture de la DB:", e);
      }
      db = null;
    }
  };

  // Ouvre la base de données et crée les object stores si nécessaire
  const openDB = async (datasetToEnsure) => {
    // Réutilise la connexion existante si possible
    if (db && db.objectStoreNames && db.objectStoreNames.contains(datasetToEnsure)) {
      return db;
    }

    // Ferme la connexion existante si un nouveau store est nécessaire
    if (db && datasetToEnsure && (!db.objectStoreNames || !db.objectStoreNames.contains(datasetToEnsure))) {
      closeDB();
      dbVersion++;
    }

    return new Promise((resolve, reject) => {
      let resolved = false;
      const safeResolve = (value) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          resolve(value);
        }
      };
      const safeReject = (error) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          reject(error);
        }
      };

      let request;

      try {
        request = indexedDB.open(dbName, dbVersion);
      } catch (error) {
        reject(new Error("Impossible d'ouvrir IndexedDB: " + error.message));
        return;
      }

      // Timeout de sécurité pour Safari
      const timeoutId = setTimeout(() => {
        safeReject(new Error("Timeout lors de l'ouverture de la base de données"));
      }, 10000);

      request.onupgradeneeded = ({ target }) => {
        const database = target.result;
        const transaction = target.transaction;

        const datasets = datasetToEnsure
          ? [activeList.file, datasetToEnsure].filter(Boolean)
          : [activeList.file].filter(Boolean);

        datasets.forEach(datasetName => {
          if (!datasetName) return;

          let store;
          try {
            if (database.objectStoreNames.contains(datasetName)) {
              store = transaction.objectStore(datasetName);
            } else {
              const keyPathField = helpers.correlateFields("ahri", g_listOrigin);
              store = database.createObjectStore(datasetName, { keyPath: keyPathField });
            }

            const requiredIndexes = REQUIRED_INDEXES[g_listOrigin] || [];
            requiredIndexes.forEach(indexName => {
              try {
                if (!store.indexNames.contains(indexName)) {
                  store.createIndex(indexName, indexName, { unique: false });
                }
              } catch (indexError) {
                console.warn(`Impossible de créer l'index ${indexName}:`, indexError);
              }
            });
          } catch (storeError) {
            console.error(`Erreur lors de la création du store ${datasetName}:`, storeError);
          }
        });
      };

      request.onsuccess = ({ target }) => {
        db = target.result;

        db.onerror = (event) => {
          console.error("Erreur de base de données:", event.target.error);
        };

        db.onversionchange = () => {
          closeDB();
          console.warn("Base de données mise à jour ailleurs, connexion fermée");
        };

        safeResolve(db);
      };

      request.onerror = async ({ target }) => {
        const error = target.error || new Error("Erreur IndexedDB inconnue");

        if (error.name === "VersionError") {
          try {
            dbVersion = await getCurrentDbVersion();
            dbVersion++;
            const retryResult = await openDB(datasetToEnsure);
            safeResolve(retryResult);
          } catch (versionError) {
            safeReject(versionError);
          }
        } else {
          safeReject(error);
        }
      };

      request.onblocked = () => {
        console.warn("Ouverture de la base de données bloquée. Fermez les autres onglets.");
        closeDB();
        // Safari: tenter de résoudre après fermeture
        setTimeout(() => {
          if (!resolved) {
            openDB(datasetToEnsure).then(safeResolve).catch(safeReject);
          }
        }, 100);
      };
    });
  };

  // Sauvegarde les données dans la base de données avec batching pour Safari
  const saveDataToDB = async (dataset, data) => {
    console.log(`Sauvegarde des données dans IndexedDB pour le dataset: ${data.length} items`);
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error("Les données doivent être un tableau non vide");
    }

    const database = await openDB(dataset);

    if (!database.objectStoreNames.contains(dataset)) {
      dbVersion++;
      const newDatabase = await openDB(dataset);

      if (!newDatabase.objectStoreNames.contains(dataset)) {
        throw new Error(`Impossible de créer l'object store '${dataset}'`);
      }
    }

    const keyField = helpers.correlateFields("ahri", g_listOrigin);
    const validItems = data
      .filter(item => item && item[keyField])
      .map(item => {
        if (item["TTG_Ext5"]) {
          item["TTG_colBonification"] = parseFloat(item["TTG_Ext5"]) - parseFloat(item["TTG_AF_EffAlone"] || 0);
        }
        return item;
      });

    // Safari gère mieux les petits batches
    const BATCH_SIZE = isSafari() ? 100 : 500;
    let totalSaved = 0;

    for (let i = 0; i < validItems.length; i += BATCH_SIZE) {
      const batch = validItems.slice(i, i + BATCH_SIZE);
      const savedCount = await saveBatch(database, dataset, batch);
      totalSaved += savedCount;

      // Safari: pause entre les batches pour éviter les timeouts de transaction
      if (isSafari() && i + BATCH_SIZE < validItems.length) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
    }
console.log(`Sauvegarde terminée: ${totalSaved} items sauvegardés sur ${validItems.length}`);
    return totalSaved;
  };

  // Sauvegarde un batch de données
  const saveBatch = (database, dataset, items) => {
    return new Promise((resolve, reject) => {
      let transaction;

      try {
        transaction = database.transaction([dataset], "readwrite");
      } catch (error) {
        reject(new Error("Impossible de créer la transaction: " + error.message));
        return;
      }

      const store = transaction.objectStore(dataset);
      let itemCount = 0;
      let errorCount = 0;

      items.forEach(item => {
        try {
          const request = store.put(item);
          request.onsuccess = () => {
            itemCount++;
          };
          request.onerror = (event) => {
            errorCount++;
            console.warn("Erreur lors de l'ajout d'un item:", event.target.error);
            // Empêcher l'annulation de la transaction pour une erreur individuelle
            event.preventDefault();
          };
        } catch (error) {
          errorCount++;
          console.warn("Exception lors de l'ajout d'un item:", error);
        }
      });

      transaction.oncomplete = () => {
        if (errorCount > 0) {
          console.warn(`${errorCount} items n'ont pas pu être sauvegardés`);
        }
        resolve(itemCount);
      };

      transaction.onerror = ({ target }) => {
        const error = target.error || new Error("Erreur de transaction");
        console.error("Erreur lors de la sauvegarde:", error);
        reject(error);
      };

      transaction.onabort = ({ target }) => {
        console.error("Transaction annulée:", target.error);
        reject(target.error || new Error("Transaction annulée"));
      };
    });
  };

  // Charge les données depuis la base de données
  const loadDataFromDB = async (dataset) => {
    try {
      const database = await openDB(dataset);

      if (!database.objectStoreNames.contains(dataset)) {
        console.warn(`Object store '${dataset}' n'existe pas`);
        return [];
      }

      return new Promise((resolve, reject) => {
        let transaction;

        try {
          transaction = database.transaction([dataset], "readonly");
        } catch (error) {
          reject(new Error("Impossible de créer la transaction de lecture: " + error.message));
          return;
        }

        const store = transaction.objectStore(dataset);

        // Safari 15+ supporte getAll()
        if (typeof store.getAll === "function") {
          const request = store.getAll();

          request.onsuccess = ({ target }) => {
            const result = target.result || [];
            resolve(result);
          };

          request.onerror = ({ target }) => {
            console.warn("getAll() a échoué, tentative avec curseur");
            loadDataWithCursor(store, resolve, reject);
          };
        } else {
          loadDataWithCursor(store, resolve, reject);
        }

        // Safari: gestion de l'erreur de transaction
        transaction.onerror = ({ target }) => {
          console.error("Erreur de transaction lecture:", target.error);
        };
      });
    } catch (error) {
      console.error(`Erreur lors du chargement de ${dataset}:`, error);
      return [];
    }
  };

  // Charge les données avec un curseur (fallback)
  const loadDataWithCursor = (store, resolve, reject) => {
    const results = [];
    let request;

    try {
      request = store.openCursor();
    } catch (error) {
      reject(new Error("Impossible d'ouvrir le curseur: " + error.message));
      return;
    }

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        results.push(cursor.value);
        try {
          cursor.continue();
        } catch (error) {
          console.warn("Erreur cursor.continue(), retour des résultats partiels");
          resolve(results);
        }
      } else {
        resolve(results);
      }
    };

    request.onerror = ({ target }) => {
      console.error("Erreur lors du chargement avec curseur:", target.error);
      // Retourner les résultats partiels au lieu de rejeter
      resolve(results);
    };
  };

  // Récupère les éléments de la base de données avec pagination, tri et filtrage
  const getTopItemsFromDB = async (dataset, count, { sortOptions = null, searchOptions = null, paginationOptions = null } = {}) => {
    console.log(`getTopItemsFromDB: dataset=${dataset}, count=${count}`);

    const currentPage = paginationOptions?.page || 1;
    const itemsPerPage = count;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Chemin rapide: le dataset est déjà en cache mémoire (cas normal après le premier
    // chargement). Filtrage/tri/pagination se font en JS synchrone, sans rouvrir de
    // transaction ni de curseur IndexedDB.
    const cachedItems = getMemoryCache(dataset);
    if (cachedItems) {
      return getTopItemsFromMemory(cachedItems, { sortOptions, searchOptions, currentPage, itemsPerPage, startIndex, endIndex });
    }

    // Repli: scan par curseur IndexedDB (cache mémoire pas encore disponible)
    let database;
    try {
      database = await openDB(dataset);
    } catch (error) {
      console.error("Erreur lors de l'ouverture de la DB:", error);
      return createEmptyPaginationResult(count);
    }

    if (!database.objectStoreNames.contains(dataset)) {
      return createEmptyPaginationResult(count);
    }

    let transaction;
    try {
      transaction = database.transaction([dataset], "readonly");
    } catch (error) {
      console.error("Erreur lors de la création de la transaction:", error);
      return createEmptyPaginationResult(count);
    }

    const store = transaction.objectStore(dataset);

    let request;
    try {
      request = getCursor(store, sortOptions);
    } catch (error) {
      console.error("Erreur lors de l'obtention du curseur:", error);
      return createEmptyPaginationResult(count);
    }

    const allMatchingItems = [];
    const paginatedItems = [];

    return new Promise((resolve) => {
      let resolved = false;
      const safeResolve = (value) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          resolve(value);
        }
      };

      // Timeout de sécurité pour Safari
      const timeoutId = setTimeout(() => {
        console.warn("Timeout lors de la recherche, retour des résultats partiels");
        safeResolve(createPaginationResult(paginatedItems, {
          currentPage,
          totalItems: allMatchingItems.length,
          totalPages: Math.ceil(allMatchingItems.length / itemsPerPage),
          itemsPerPage,
          hasNextPage: false,
          hasPreviousPage: currentPage > 1,
          startIndex: startIndex + 1,
          endIndex: Math.min(endIndex, allMatchingItems.length),
          partial: true
        }));
      }, 30000);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          let addItem = true;

          if (searchOptions) {
            addItem = matchesCriteria(cursor.value, searchOptions);
          }

          if (addItem) {
            allMatchingItems.push(cursor.value);
            if (allMatchingItems.length > startIndex && allMatchingItems.length <= endIndex) {
              paginatedItems.push(cursor.value);
            }
          }

          try {
            cursor.continue();
          } catch (error) {
            console.error("Erreur lors de cursor.continue():", error);
            finalizePagination();
          }
        } else {
          finalizePagination();
        }

        function finalizePagination() {
          const totalItems = allMatchingItems.length;
          const totalPages = Math.ceil(totalItems / itemsPerPage);

          window.allMatchingItemsGlobal = allMatchingItems;

          safeResolve(createPaginationResult(paginatedItems, {
            currentPage,
            totalItems,
            totalPages,
            itemsPerPage,
            hasNextPage: currentPage < totalPages,
            hasPreviousPage: currentPage > 1,
            startIndex: startIndex + 1,
            endIndex: Math.min(endIndex, totalItems)
          }));
        }
      };

      request.onerror = (event) => {
        console.error("Erreur lors de la recherche:", event.target.error);
        safeResolve(createEmptyPaginationResult(count));
      };

      // Safari: gestion de l'erreur/abort de transaction
      transaction.onerror = () => {
        safeResolve(createPaginationResult(paginatedItems, {
          currentPage,
          totalItems: allMatchingItems.length,
          totalPages: Math.ceil(allMatchingItems.length / itemsPerPage),
          itemsPerPage,
          hasNextPage: false,
          hasPreviousPage: currentPage > 1,
          startIndex: startIndex + 1,
          endIndex: Math.min(endIndex, allMatchingItems.length),
          partial: true
        }));
      };
    });
  };

  // Crée un résultat de pagination vide
  const createEmptyPaginationResult = (count) => {
    return {
      items: [],
      pagination: {
        currentPage: 1,
        totalItems: 0,
        totalPages: 0,
        itemsPerPage: count,
        hasNextPage: false,
        hasPreviousPage: false
      }
    };
  };

  // Obtient le curseur approprié en fonction des options de tri
  const getCursor = (store, sortOptions) => {
    if (sortOptions?.field) {
      try {
        if (store.indexNames.contains(sortOptions.field)) {
          const index = store.index(sortOptions.field);
          const direction = sortOptions.direction === "desc" ? "prev" : "next";
          return index.openCursor(null, direction);
        } else {
          console.warn(`L'index '${sortOptions.field}' n'existe pas. Utilisation du curseur par défaut.`);
          return store.openCursor();
        }
      } catch (error) {
        console.warn("Erreur lors de l'accès à l'index:", error);
        return store.openCursor();
      }
    }
    return store.openCursor();
  };

  // Crée l'objet résultat avec les informations de pagination
  const createPaginationResult = (items, pagination) => {
    return {
      items,
      pagination
    };
  };

  // Traite les critères de recherche pour un item donné (utilisé par le chemin mémoire
  // et par le chemin curseur IndexedDB, ce dernier passant cursor.value comme item)
  const matchesCriteria = (item, searchOptions) => {
    let addItem = true;

    if (searchOptions.field && searchOptions.term) {
      const itemValue = String(item[searchOptions.field] || "");
      if (!itemValue.toLowerCase().includes(searchOptions.term.toLowerCase())) {
        addItem = false;
      }
    }

    if (addItem && searchOptions.criteria && Array.isArray(searchOptions.criteria)) {
      for (const criterion of searchOptions.criteria) {
        const isValidCriterion = (criterion.field || criterion.fields) && (
          (criterion.term !== undefined && criterion.term !== "") ||
          criterion.operator === "range" ||
          criterion.operator === "multiFieldRange"
        );

        if (!isValidCriterion) continue;

        const itemValue = String(item[criterion.field] || "");
        const searchTerm = criterion.term ? criterion.term.toString().toLowerCase() : "";

        switch (criterion.operator || "contains") {
          case "contains":
            if (!itemValue.toLowerCase().includes(searchTerm)) addItem = false;
            break;
          case "equals":
            if (itemValue.toLowerCase() !== searchTerm) addItem = false;
            break;
          case "startsWith":
            if (!itemValue.toLowerCase().startsWith(searchTerm)) addItem = false;
            break;
          case "endsWith":
            if (!itemValue.toLowerCase().endsWith(searchTerm)) addItem = false;
            break;
          case "in":
            if (Array.isArray(criterion.term)) {
              const searchTermsLower = criterion.term.map(t => t.toString().toLowerCase());
              if (!searchTermsLower.includes(itemValue.toLowerCase())) addItem = false;
            }
            break;
          case "range":
            addItem = checkRangeCriterion(itemValue, criterion.min, criterion.max);
            break;
          case "multiFieldRange":
            addItem = checkMultiFieldRangeCriterion(item, criterion);
            break;
          default:
            if (!itemValue.toLowerCase().includes(searchTerm)) addItem = false;
        }

        if (!addItem) break;
      }
    }

    return addItem;
  };

  // Trie un tableau d'items selon un champ (numérique ou texte) et une direction "asc"/"desc"
  const sortItemsByField = (items, field, direction) => {
    const dir = direction === "desc" ? -1 : 1;
    return [...items].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      const numA = parseFloat(valA);
      const numB = parseFloat(valB);
      const bothNumeric = valA !== undefined && valA !== "" && valB !== undefined && valB !== "" && !isNaN(numA) && !isNaN(numB);

      if (bothNumeric) {
        return (numA - numB) * dir;
      }

      return String(valA || "").localeCompare(String(valB || "")) * dir;
    });
  };

  // Filtre, trie et pagine les données à partir du cache mémoire (synchrone, sans IndexedDB)
  const getTopItemsFromMemory = (items, { sortOptions, searchOptions, currentPage, itemsPerPage, startIndex, endIndex }) => {
    try {
      let filteredItems = searchOptions
        ? items.filter(item => matchesCriteria(item, searchOptions))
        : items;

      if (sortOptions?.field && typeof sortOptions.field === "string") {
        filteredItems = sortItemsByField(filteredItems, sortOptions.field, sortOptions.direction);
      }

      const totalItems = filteredItems.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const paginatedItems = filteredItems.slice(startIndex, endIndex);

      // Conservé pour l'export CSV qui exporte l'ensemble des résultats filtrés (pas seulement la page courante)
      window.allMatchingItemsGlobal = filteredItems;

      return createPaginationResult(paginatedItems, {
        currentPage,
        totalItems,
        totalPages,
        itemsPerPage,
        hasNextPage: currentPage < totalPages,
        hasPreviousPage: currentPage > 1,
        startIndex: startIndex + 1,
        endIndex: Math.min(endIndex, totalItems)
      });
    } catch (error) {
      console.error("Erreur lors du filtrage/tri en mémoire:", error);
      return createEmptyPaginationResult(itemsPerPage);
    }
  };

  // Vérifie si une valeur est dans une plage
  const checkRangeCriterion = (value, min, max) => {
    const numericValue = parseFloat(value);
    const minValue = parseValidNumber(min, 0);
    const maxValue = parseValidNumber(max, Infinity);

    return !isNaN(numericValue) && numericValue >= minValue && numericValue <= maxValue;
  };

  // Vérifie si des valeurs de plusieurs champs sont dans une plage
  const checkMultiFieldRangeCriterion = (item, criterion) => {
    const fields = criterion.fields || [];
    const minVal = parseValidNumber(criterion.min, 0);
    const maxVal = parseValidNumber(criterion.max, Infinity);
    const logicalOp = criterion.logicalOperator || "OR";

    const rangeResults = fields.map(fieldName => {
      const fieldValue = item[fieldName] || "";
      const numVal = parseFloat(fieldValue);
      return !isNaN(numVal) && numVal >= minVal && numVal <= maxVal;
    });

    return logicalOp === "AND"
      ? rangeResults.every(result => result === true)
      : rangeResults.some(result => result === true);
  };

  // Convertit une valeur en nombre valide ou retourne une valeur par défaut
  const parseValidNumber = (value, defaultValue) => {
    return value !== undefined && value !== null && value !== ""
      ? parseFloat(value)
      : defaultValue;
  };

  // Construit des critères de recherche structurés
  const buildSearchCriteria = () => {
    const criteria = [];

    return {
      addTextCriteria: function(field, term, operator = "contains") {
        if (term && term.trim() !== "") {
          criteria.push({ field, term: term.trim(), operator });
        }
        return this;
      },

      addInCriteria: function(field, values) {
        if (values && Array.isArray(values) && values.length > 0) {
          criteria.push({ field, term: values, operator: "in" });
        }
        return this;
      },

      addRangeCriteria: function(field, min, max) {
        if (min !== undefined && min !== null && min !== "" ||
            max !== undefined && max !== null && max !== "") {
          criteria.push({ field, operator: "range", min, max });
        }
        return this;
      },

      addMultiFieldRangeCriteria: function(fields, min, max, logicalOperator = "OR") {
        if (Array.isArray(fields) && fields.length > 0 &&
            (min !== undefined && min !== null && min !== "" ||
             max !== undefined && max !== null && max !== "")) {
          criteria.push({
            fields, operator: "multiFieldRange", min, max, logicalOperator
          });
        }
        return this;
      },

      addCustomCriteria: function(field, term, operator) {
        if (term !== undefined && term !== null && term !== "") {
          criteria.push({ field, term, operator });
        }
        return this;
      },

      getCriteria: function() {
        return criteria.length > 0 ? criteria : null;
      },

      reset: function() {
        criteria.length = 0;
        return this;
      }
    };
  }

  // Recherche par numéro AHRI
  const searchByAHRI = async (ahriNumber) => {
    try {
      const dbData = await loadDataFromDB(dataset);
      const searchValue = ahriNumber ? ahriNumber.toString().trim() : "";
      const result = dbData.find(item => {
        const itemValue = item[helpers.correlateFields("ahri", g_listOrigin)];
        return itemValue && itemValue.toString() === searchValue;
      });
      return result;

    } catch (error) {
      if (typeof UIHelper !== "undefined" && typeof statusMessage !== "undefined") {
        UIHelper.displayErrorMessage(`Erreur: ${error.message}`, statusMessage);
      }
      if (typeof searchInput !== "undefined" && searchInput) {
        searchInput.classList.add("error");
      }

      throw error;
    }
  };

  const searchByIdES = async (idES) => {
    try {
      const dbData = await loadDataFromDB(dataset);
      const searchValue = idES ? idES.toString().trim() : "";
      const result = dbData.find(item => item.pd_id && item.pd_id.toString() === searchValue);
      return result;

    } catch (error) {
      if (typeof UIHelper !== "undefined" && typeof statusMessage !== "undefined") {
        UIHelper.displayErrorMessage(`Erreur: ${error.message}`, statusMessage);
      }
      if (typeof searchInput !== "undefined" && searchInput) {
        searchInput.classList.add("error");
      }

      throw error;
    }
  };

  // Initialise IndexedDBManager
  init();

  // Expose les fonctions publiques
  return {
    openDB,
    saveDataToDB,
    loadDataFromDB,
    getTopItemsFromDB,
    buildSearchCriteria,
    addEventListener,
    emitEvent,
    init,
    searchByAHRI,
    searchByIdES,
    closeDB,
    get dbVersion() { return dbVersion; },
    get isConnected() { return db !== null; },
    get activeDataset() { return dataset; },
    get isDataReady() { return isDataReady; }
  };
}

window.IndexedDBManager = IndexedDBManager();
export default window.IndexedDBManager;
